import type { useAuth } from "@clerk/react";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { fetchWithAuthRetry } from "../../lib/api";
import {
  createSessionRecapController,
  type SessionRecapController,
} from "../../lib/session-recap-client";
import {
  INITIAL_SESSION_RECAP_STATE,
  sessionRecapReducer,
  type SessionRecapLane,
  type SessionRecapState,
} from "../../lib/session-recap-state";

type ClerkGetToken = ReturnType<typeof useAuth>["getToken"];

export type SessionRecapHandle = { state: SessionRecapState; retry?: () => void };

/**
 * The Appointment's Recap in component-local state only: no Query or Mutation
 * cache, no router state, no storage. Retry exists only on the RD lane.
 */
export function useSessionRecap({
  appointmentId,
  lane,
  rdId,
  enabled,
  getToken,
}: {
  appointmentId: string;
  lane: SessionRecapLane;
  rdId?: string;
  enabled: boolean;
  getToken: ClerkGetToken;
}): SessionRecapHandle {
  const [state, dispatch] = useReducer(
    sessionRecapReducer,
    INITIAL_SESSION_RECAP_STATE
  );
  const getTokenRef = useRef(getToken);
  const controllerRef = useRef<SessionRecapController | null>(null);

  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  useEffect(() => {
    const controller = createSessionRecapController(
      { appointmentId, lane, enabled, ...(rdId ? { rdId } : {}) },
      (path, init) => fetchWithAuthRetry(getTokenRef.current, path, init),
      dispatch
    );
    controllerRef.current = controller;
    void controller.start();

    return () => controller.abort();
  }, [appointmentId, lane, rdId, enabled]);

  const retry = useCallback(() => {
    void controllerRef.current?.retry?.();
  }, []);

  return lane === "rd" ? { state, retry } : { state };
}
